import { supabase } from '../lib/supabase';

/**
 * Fetch all glossary terms with usage statistics
 */
export const fetchGlossaryTermsWithUsage = async () => {
  try {
    // Fetch glossary terms
    const { data: terms, error: termsError } = await supabase?.from('glossary_terms')?.select(`
        id,
        term,
        pronunciation,
        definition,
        category,
        difficulty,
        created_at,
        updated_at,
        play_id,
        ai_generated,
        review_status,
        audio_url,
        audio_generation_status,
        audio_quality_flag,
        plays:play_id (
          id,
          title
        )
      `)?.order('term', { ascending: true });

    if (termsError) throw termsError;

    // For each term, calculate usage statistics
    const termsWithUsage = await Promise.all(
      terms?.map(async (term) => {
        // Get all line assignments for this term
        const { data: assignments, error: assignmentsError } = await supabase?.from('line_glossary_assignments')?.select(`
            id,
            line_id,
            lines:line_id (
              id,
              play_id,
              plays:play_id (
                id,
                title
              )
            )
          `)?.eq('glossary_term_id', term?.id);

        if (assignmentsError) {
          console.error('Error fetching assignments:', assignmentsError);
          return {
            ...term,
            totalUsageCount: 0,
            playUsage: []
          };
        }

        // Calculate usage per play
        const playUsageMap = {};
        
        assignments?.forEach((assignment) => {
          const playId = assignment?.lines?.plays?.id;
          const playTitle = assignment?.lines?.plays?.title;
          
          if (playId && playTitle) {
            if (!playUsageMap?.[playId]) {
              playUsageMap[playId] = {
                playId,
                playTitle,
                count: 0
              };
            }
            playUsageMap[playId].count++;
          }
        });

        const playUsage = Object.values(playUsageMap);
        const totalUsageCount = playUsage?.reduce((sum, play) => sum + play?.count, 0);

        return {
          id: term?.id,
          word: term?.term,
          pronunciation: term?.pronunciation,
          definition: term?.definition,
          category: term?.category,
          difficulty: term?.difficulty,
          playTitle: term?.plays?.title || null,
          playId: term?.play_id,
          aiGenerated: term?.ai_generated,
          reviewStatus: term?.review_status,
          audioUrl: term?.audio_url,
          audioGenerationStatus: term?.audio_generation_status,
          audioQualityFlag: term?.audio_quality_flag,
          totalUsageCount,
          playUsage,
          lastUpdated: new Date(term.updated_at)
        };
      })
    );

    return termsWithUsage;
  } catch (error) {
    console.error('Error fetching glossary terms with usage:', error);
    throw error;
  }
};

/**
 * Fetch all plays for filter dropdown
 */
export const fetchPlays = async () => {
  try {
    const { data, error } = await supabase?.from('plays')?.select('id, title')?.order('title', { ascending: true });

    if (error) throw error;

    return data?.map(play => ({
      id: play?.id,
      title: play?.title
    })) || [];
  } catch (error) {
    console.error('Error fetching plays:', error);
    throw error;
  }
};

/**
 * Update a glossary term
 */
export const updateGlossaryTerm = async (id, updates) => {
  try {
    const { data, error } = await supabase?.from('glossary_terms')?.update({
        term: updates?.word,
        pronunciation: updates?.pronunciation,
        definition: updates?.definition,
        updated_at: new Date()?.toISOString()
      })?.eq('id', id)?.select()?.single();

    if (error) throw error;

    return data;
  } catch (error) {
    console.error('Error updating glossary term:', error);
    throw error;
  }
};

/**
 * Delete a glossary term
 */
export const deleteGlossaryTerm = async (id) => {
  try {
    const { error } = await supabase?.from('glossary_terms')?.delete()?.eq('id', id);

    if (error) throw error;

    return true;
  } catch (error) {
    console.error('Error deleting glossary term:', error);
    throw error;
  }
};

/**
 * Add a new glossary term
 */
export const addGlossaryTerm = async (newTerm, userId) => {
  try {
    const { data, error } = await supabase?.from('glossary_terms')?.insert({
        term: newTerm?.word,
        pronunciation: newTerm?.pronunciation,
        definition: newTerm?.definition,
        play_id: newTerm?.playId || null,
        category: newTerm?.category || null,
        difficulty: newTerm?.difficulty || 'medium',
        created_by: userId
      })?.select()?.single();

    if (error) throw error;

    return data;
  } catch (error) {
    console.error('Error adding glossary term:', error);
    throw error;
  }
};
